// src/services/avatarService.ts
import { supabase } from '../lib/supabase'

const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2MB
const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
]

export interface AvatarUploadResult {
  success: boolean
  url?: string
  error?: string
}

/**
 * Validate file before upload
 */
export const validateAvatarFile = (
  file: File
): { valid: boolean; error?: string } => {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File size must be less than ${MAX_FILE_SIZE / 1024 / 1024}MB`,
    }
  }

  // Check file type
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: 'File type must be JPEG, PNG, WebP, or GIF',
    }
  }

  return { valid: true }
}

/**
 * Upload avatar to Supabase storage
 */
export const uploadAvatar = async (
  userId: string,
  file: File
): Promise<AvatarUploadResult> => {
  try {
    // Validate file
    const validation = validateAvatarFile(file)
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error,
      }
    }

    // Generate unique filename
    const fileExt = file.name.split('.').pop()
    const fileName = `${userId}/avatar-${Date.now()}.${fileExt}`

    // Delete old avatar if exists
    await deleteOldAvatar(userId)

    // Upload new avatar
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true,
      })

    if (uploadError) {
      console.error('Upload error:', uploadError)
      return {
        success: false,
        error: uploadError.message || 'Failed to upload avatar',
      }
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('avatars')
      .getPublicUrl(fileName)

    if (!urlData?.publicUrl) {
      return {
        success: false,
        error: 'Failed to get avatar URL',
      }
    }

    return {
      success: true,
      url: urlData.publicUrl,
    }
  } catch (error) {
    console.error('Avatar upload error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    }
  }
}

/**
 * Delete old avatar from storage
 */
const deleteOldAvatar = async (userId: string): Promise<void> => {
  try {
    // List all files in user's folder
    const { data: files, error: listError } = await supabase.storage
      .from('avatars')
      .list(userId)

    if (listError || !files || files.length === 0) {
      return // No old files to delete
    }

    // Delete all old files
    const filePaths = files.map(file => `${userId}/${file.name}`)
    await supabase.storage.from('avatars').remove(filePaths)
  } catch (error) {
    console.warn('Error deleting old avatar:', error)
    // Don't throw - this is not critical
  }
}

/**
 * Delete avatar and revert to default
 */
export const deleteAvatar = async (
  userId: string
): Promise<AvatarUploadResult> => {
  try {
    await deleteOldAvatar(userId)

    return {
      success: true,
    }
  } catch (error) {
    console.error('Avatar deletion error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete avatar',
    }
  }
}

/**
 * Get avatar URL from storage path
 */
export const getAvatarUrl = (path: string): string => {
  const { data } = supabase.storage.from('avatars').getPublicUrl(path)

  return data?.publicUrl || ''
}

/**
 * Generate a placeholder avatar URL using external service
 */
export const getPlaceholderAvatar = (name?: string, email?: string): string => {
  const seed = name || email || 'default'
  return `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(seed)}`
}
